import { useState, useEffect, useRef, useCallback } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import {
  Package, ShoppingBag, Tag, Settings, Bell, Search,
  LogOut, Menu, ShoppingCart, Mail, Home, Database,
  Truck, FileText, FolderTree, LayoutDashboard, Users,
  X, ChevronDown, ChevronRight, Upload, Download, BarChart2, Crown, Smartphone,
} from "lucide-react";import { ADMIN, AUTH } from "@/constants/testIds";

/* ── New-order bell ── checks for orders every minute; unread = placed after the bell was last opened */
const BELL_API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BELL_SEEN_KEY = "it_orders_seen_at";

function readSeenAt() {
  try {
    const v = localStorage.getItem(BELL_SEEN_KEY);
    if (v) return v;
  } catch { /* storage blocked */ }
  return new Date(Date.now() - 24 * 3600 * 1000).toISOString(); // first visit: last 24 hours count as new
}

function OrderBell() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [seenAt, setSeenAt] = useState(readSeenAt);
  const [highlightAfter, setHighlightAfter] = useState(seenAt);
  const [perm, setPerm] = useState(() => ("Notification" in window ? Notification.permission : "unsupported"));
  const known = useRef(null);
  const wrapRef = useRef(null);

  const poll = useCallback(async () => {
    const token = localStorage.getItem("it_token");
    if (!token) return;
    try {
      const res = await axios.get(`${BELL_API}/admin/orders?limit=20`, { headers: { Authorization: `Bearer ${token}` } });
      const list = Array.isArray(res.data) ? res.data : [];
      setOrders(list);
      if (known.current && "Notification" in window && Notification.permission === "granted") {
        list.filter((o) => !known.current.has(o.id)).forEach((o) => {
          try {
            new Notification(`New order ${o.order_number || ""}`, {
              body: `${o.customer_name || "Customer"} · ₹${Number(o.total || 0).toLocaleString("en-IN")} · ${o.payment_method || ""}`,
            });
          } catch { /* ignore */ }
        });
      }
      known.current = new Set(list.map((o) => o.id));
    } catch { /* keep the last list */ }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 60000);
    return () => clearInterval(t);
  }, [poll]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const seenMs = Date.parse(seenAt) || 0;
  const unseen = orders.filter((o) => (Date.parse(o.created_at) || 0) > seenMs);
  const highlightMs = Date.parse(highlightAfter) || 0;

  const toggle = () => {
    if (!open) {
      const now = new Date().toISOString();
      setHighlightAfter(seenAt);
      setSeenAt(now);
      try { localStorage.setItem(BELL_SEEN_KEY, now); } catch { /* storage blocked */ }
    }
    setOpen((v) => !v);
  };

  const enableAlerts = async () => {
    try { setPerm(await Notification.requestPermission()); } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button onClick={toggle} data-testid="admin-order-bell" title="New orders"
        className="relative text-slate-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/5">
        <Bell className="w-5 h-5" />
        {unseen.length > 0 && (
          <span data-testid="admin-order-bell-count"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
            {unseen.length > 9 ? "9+" : unseen.length}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white text-slate-800 rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="admin-order-bell-panel">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <p className="text-sm font-bold">Recent orders</p>
            <button onClick={() => { setOpen(false); navigate("/admin/orders"); }} className="text-xs text-orange-600 font-semibold hover:underline">View all</button>
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
            {orders.length === 0 && <p className="px-4 py-6 text-xs text-slate-500 text-center">No orders yet</p>}
            {orders.slice(0, 8).map((o) => {
              const isNew = (Date.parse(o.created_at) || 0) > highlightMs;
              return (
                <button key={o.id} onClick={() => { setOpen(false); navigate(`/admin/orders?order=${o.id}`); }}
                  className={`w-full text-left px-4 py-2.5 hover:bg-slate-50 ${isNew ? "bg-orange-50" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold text-slate-900">{o.order_number}</span>
                    <span className="text-xs font-semibold">₹{Number(o.total || 0).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <span className="text-[11px] text-slate-500 truncate">{o.customer_name} · {o.payment_method || "COD"}</span>
                    <span className="text-[10px] text-slate-400 whitespace-nowrap">
                      {isNew && <span className="text-orange-600 font-bold mr-1">NEW</span>}
                      {o.created_at ? new Date(o.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          {perm === "default" && (
            <button onClick={enableAlerts} className="w-full px-4 py-2.5 text-xs font-semibold text-orange-600 border-t border-slate-100 hover:bg-orange-50">
              Turn on desktop alerts for new orders
            </button>
          )}
          {perm === "denied" && (
            <p className="px-4 py-2.5 text-[11px] text-slate-500 border-t border-slate-100">Desktop alerts are blocked in this browser&apos;s site settings.</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Section label ──────────────────────────────────── */
function NavSection({ label }) {
  return (
    <p className="px-3 pt-5 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 select-none">
      {label}
    </p>
  );
}

/* ── Single nav item ────────────────────────────────── */
function NavItem({ icon: Icon, label, to, testId, badge }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        `sidebar-nav-item${isActive ? " active" : ""}`
      }
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{label}</span>
      {badge && (
        <span className="bg-orange-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none flex-shrink-0 min-w-[20px] text-center">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

/* ── Collapsible group ──────────────────────────────── */
function NavGroup({ icon: Icon, label, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        className="sidebar-nav-item w-full"
        onClick={() => setOpen(v => !v)}
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1 text-left whitespace-nowrap">{label}</span>
        {open
          ? <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 text-slate-500" />
          : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 text-slate-500" />}
      </button>
      {open && (
        <div className="ml-3 mt-0.5 pl-3 border-l border-slate-700/60 space-y-0.5">
          {children}
        </div>
      )}
    </div>
  );
}

/* ── Main layout ────────────────────────────────────── */
export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success("Logged out");
    navigate("/login");
  };

  const sidebarContent = (
    <div className="flex flex-col h-full bg-[#0f172a] overflow-hidden">

      {/* ── Brand ── */}
      <div className="px-4 py-5 flex-shrink-0 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-orange-600 flex items-center justify-center flex-shrink-0 shadow-lg">
            <ShoppingCart className="w-4.5 h-4.5 text-white" style={{ width: 18, height: 18 }} />
          </div>
          <div className="min-w-0">
            <BrandLogo size="sm" textVariant="light" />
            <p className="text-[10px] text-slate-500 leading-tight mt-0.5">Import Smart. Sell Better.</p>
          </div>
        </div>
      </div>

      {/* ── Nav ── */}
      <nav className="flex-1 overflow-y-auto px-2 pb-4 scrollbar-hide">

        <NavSection label="Main" />
        <NavItem icon={LayoutDashboard} label="Dashboard"        to="/admin/dashboard"   testId={ADMIN.sidebarDashboard} />
        <NavItem icon={BarChart2}       label="Analytics"        to="/admin/analytics" />
        <NavItem icon={Users}           label="Customers"        to="/admin/customers"   testId={ADMIN.sidebarCustomers} />
        <NavItem icon={ShoppingBag}     label="Orders"           to="/admin/orders"      testId={ADMIN.sidebarOrders} />

        <NavGroup icon={Package} label="Products" defaultOpen={true}>
          <NavLink
            to="/admin/products"
            end
            data-testid={ADMIN.sidebarPanelProducts}
            className={({ isActive }) =>
              `sidebar-nav-item text-xs${isActive ? " active" : ""}`
            }
          >
            <div className="w-1.5 h-1.5 rounded-full bg-current flex-shrink-0" />
            <span className="whitespace-nowrap">All Products</span>
          </NavLink>
          <button
            data-testid={ADMIN.sidebarImportProducts}
            className="sidebar-nav-item text-xs w-full"
            onClick={() => navigate("/admin/products")}
          >
            <Upload className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="whitespace-nowrap">Import Products</span>
          </button>
          <button
            data-testid={ADMIN.sidebarExportProducts}
            className="sidebar-nav-item text-xs w-full"
            onClick={() => navigate("/admin/products?export=1")}
          >
            <Download className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="whitespace-nowrap">Export Products</span>
          </button>
        </NavGroup>

        <NavItem icon={FolderTree}  label="Categories"        to="/admin/categories" />
        <NavItem icon={FileText}    label="Pages"              to="/admin/pages" />
        <NavItem icon={Home}        label="Homepage Builder"   to="/admin/homepage" />

        <NavSection label="Management" />
        <NavItem icon={Mail}          label="Email Notifications"    to="/admin/email-notifications" />
        <NavItem icon={Truck}         label="Checkout Settings"      to="/admin/checkout-settings" />
        <NavItem icon={Tag}           label="Coupons"                to="/admin/coupons" />
        <NavItem icon={Crown}         label="Subscription Plans"     to="/admin/subscription-plans" />
        <NavItem icon={Users}         label="Customer Subscriptions" to="/admin/customer-subscriptions" />
        <NavItem icon={Smartphone}    label="Mobile Account Page"    to="/admin/mobile-account" />

        <NavSection label="Settings" />
        <NavItem icon={Settings}  label="Storefront Settings"  to="/admin/settings" />
        <NavItem icon={Database}  label="Demo Data Seeder"     to="/admin/seed" />

      </nav>

      {/* ── User footer ── */}
      <div className="px-3 py-3 border-t border-white/5 flex-shrink-0">
        <div className="flex items-center gap-2.5 px-1">
          <div className="w-7 h-7 rounded-full bg-orange-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            {user?.name?.[0]?.toUpperCase() || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-200 truncate">{user?.name || "Admin"}</p>
            <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
          </div>
          <button
            data-testid={AUTH.logoutButton}
            onClick={handleLogout}
            className="text-slate-500 hover:text-red-400 transition-colors flex-shrink-0 p-1 rounded hover:bg-white/5"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-56 flex-col fixed left-0 top-0 bottom-0 z-30 shadow-xl">
        {sidebarContent}
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-56 flex flex-col shadow-xl">
            {sidebarContent}
          </div>
          <div className="flex-1 bg-black/60" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 lg:ml-56 flex flex-col min-h-screen">

        {/* Top header */}
        <header className="bg-[#0f172a] border-b border-white/5 px-4 py-3 flex items-center gap-4 sticky top-0 z-20">
          <button
            className="lg:hidden text-slate-400 hover:text-white transition-colors"
            onClick={() => setMobileOpen(v => !v)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <div className="flex-1 max-w-lg">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                placeholder="Search products, orders, customers…"
                className="w-full pl-9 pr-4 py-2 bg-white/5 border border-white/8 rounded-lg text-sm text-slate-300 placeholder-slate-500 focus:outline-none focus:border-orange-500/50 transition-colors"
                style={{ borderColor: "rgba(255,255,255,0.08)" }}
              />
            </div>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            <OrderBell />
            <div className="flex items-center gap-2 pl-2 border-l border-white/10">
              <div className="w-7 h-7 rounded-full bg-orange-600 flex items-center justify-center text-white text-xs font-bold">
                {user?.name?.[0]?.toUpperCase() || "A"}
              </div>
              <span className="text-slate-300 text-sm hidden sm:block font-medium">
                {user?.name?.split(" ")[0] || "Admin"}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-slate-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
